import { walkTaxonomyTreeToSelectionLists } from '@revelio/data-access';

import type { TreeData } from '../../components/tree/useTreeApi';
import { normalizeSuggestedTitles } from '../../components/tree/utils/normalizeSuggestedTitles';
import type { TaxonomyLevelMetadata } from './taxonomyTree';

export type ParentLinkedItem = {
  id: string;
  shortName: string;
  longName: string;
  nodeCount?: number;
  // Suggested titles from the taxonomy tree (when fetched with includeTitles,
  // e.g. role/skill trees). Exposed as `rawTitles` — the key the breadcrumb
  // tree maps to a node's suggestedTitles.
  rawTitles?: string[];
  // Each ancestor level's raw id, keyed by that ancestor's level tag — e.g. an
  // msa item carries both `region` and `country`; a rics_k400 item carries both
  // `rics_k10` and `rics_k50`. Consumers read whichever linkage they need.
  [ancestorLevelTag: string]: string | number | string[] | undefined;
};

/**
 * Dashboard adapter over the shared {@link walkTaxonomyTreeToSelectionLists}
 * walk: walks normalized `TreeData` (carrying its own `depth`) and enriches each
 * item with `nodeCount` and normalized `rawTitles`. `keyForLevel` defaults to the
 * BE level tag (right for rics/geo); role passes an id-based resolver so it
 * doesn't depend on the BE tag strings (see treeToRoleSelectionLists).
 */
export const treeToParentLinkedSelectionLists = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[],
  keyForLevel: (level: TaxonomyLevelMetadata) => string | null = (level) =>
    level.tag ?? null
): Partial<Record<string, ParentLinkedItem[]>> =>
  walkTaxonomyTreeToSelectionLists<
    TreeData,
    ParentLinkedItem,
    TaxonomyLevelMetadata
  >({
    rootNodes: treeData,
    levels,
    keyForLevel,
    accessNode: (node) => ({
      id: node.id,
      name: node.name,
      depth: node.depth,
      children: node.children,
    }),
    buildItem: (base, node) => {
      const titles = normalizeSuggestedTitles(node.taxonomyTitles);
      return {
        ...base,
        ...(node.nodeCount !== undefined ? { nodeCount: node.nodeCount } : {}),
        ...(titles ? { rawTitles: titles } : {}),
      };
    },
  });
